// THE geo engine of experts.co.il (Founder 2026-09-30, Ramla → Modiin incident: "we keep having city-distance bugs — a proper
// engine: coordinates + km between cities"). ONE place for: city-name → verified coordinates (CBS localities list, cross-checked
// with OpenStreetMap — lib/geoLocalities.ts, generated by scripts/geo/build-localities.py), name aliases (תל אביב-יפו, מודיעין
// מכבים רעות, קרית/קריית, תקוה/תקווה, ת"א, פ"ת…), and every city distance (haversine on those coordinates).
// Isomorphic (no DB import) — server routes and client components call the same functions. Golden tests:
// scripts/qa/geo-golden.ts (a deploy-safe gate). NEVER compute a city distance or resolve a city name anywhere else.
import { GEO_LOCALITIES } from './geoLocalities';

export type GeoCity = { name: string; lat: number; lng: number; pop: number; code: number };
export type LatLng = { lat: number; lng: number };

/** Great-circle distance in km between two lat/lng points. */
export function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371;
  const dLat = (bLat - aLat) * Math.PI / 180;
  const dLng = (bLng - aLng) * Math.PI / 180;
  const la1 = aLat * Math.PI / 180, la2 = bLat * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Spelling-insensitive key of a city name: drops ", ישראל" and anything after the first comma, תל אביב-יפו → תל אביב,
 *  quotes/gershayim, hyphens and spaces; collapses the Hebrew plene doubles (קריית/קרית, תקווה/תקוה, גרבייה/גרביה). */
function baseKey(s?: string | null): string {
  return String(s || '')
    .replace(/,?\s*(ישראל|israel)\s*$/i, '')
    .split(',')[0]
    .replace(/תל[\s\-־]*אביב[\s\-־]*יפו/g, 'תל אביב')
    .replace(/[״"׳'`’\-־–\s().]/g, '')
    .toLowerCase();
}
export function cityKey(s?: string | null): string {
  return baseKey(s).replace(/יי/g, 'י').replace(/וו/g, 'ו');
}

// Aliases → the name the site uses. Keys go through cityKey. Abbreviations, former names, CBS long forms, Latin names.
const ALIAS_RAW: Record<string, string> = {
  'ת"א': 'תל אביב', 'תל אביב יפו': 'תל אביב', 'יפו': 'תל אביב', 'tel aviv': 'תל אביב', 'jerusalem': 'ירושלים', 'haifa': 'חיפה',
  'פ"ת': 'פתח תקווה', 'ב"ש': 'באר שבע', 'ק"ג': 'קריית גת', 'ר"ג': 'רמת גן', 'ראשל"צ': 'ראשון לציון', 'ראשון': 'ראשון לציון',
  'כ"ס': 'כפר סבא', 'ק"ש': 'קריית שמונה', 'ק"א': 'קריית אתא', 'ק"ב': 'קריית ביאליק',
  'מודיעין': 'מודיעין-מכבים-רעות', 'מודיעין מכבים רעות': 'מודיעין-מכבים-רעות', 'מכבים': 'מודיעין-מכבים-רעות', 'רעות': 'מודיעין-מכבים-רעות',
  'יהוד': 'יהוד-מונוסון', 'נצרת עילית': 'נוף הגליל', 'רמלא': 'רמלה', 'עכא': 'עכו',
  'יוקנעם': 'יקנעם עילית', 'יוקנעם עילית': 'יקנעם עילית', 'יקנעם': 'יקנעם עילית',
  'פרדס חנה': 'פרדס חנה-כרכור', 'כרכור': 'פרדס חנה-כרכור', 'קדימה': 'קדימה-צורן', 'צורן': 'קדימה-צורן',
  'בנימינה': 'בנימינה-גבעת עדה', 'גבעת עדה': 'בנימינה-גבעת עדה', 'מעלות': 'מעלות-תרשיחא', 'תרשיחא': 'מעלות-תרשיחא',
  'תמרה': 'טמרה', 'באקה': 'באקה אל-גרביה', 'אום אל פחם': 'אום אל-פחם', 'דאלית אל כרמל': 'דאלית אל-כרמל',
};

// Names the site uses that are not CBS localities (a region / a neighbourhood with its own pages) — kept with fixed points.
const SITE_ONLY: GeoCity[] = [
  { name: 'קריות', lat: 32.8390, lng: 35.0850, pop: 0, code: 0 },        // קריית ביאליק/מוצקין/ים/אתא — region pages
  { name: 'הרצליה פיתוח', lat: 32.1650, lng: 34.8040, pop: 0, code: 0 },
];

const exactKey = baseKey; // spelling kept as written — checked before the plene collapse
const BY_EXACT = new Map<string, GeoCity>();   // wins over BY_KEY: "צוקים" ≠ "צוקי ים" once יי collapses
const BY_KEY = new Map<string, GeoCity>();
for (const [name, lat, lng, pop, code] of GEO_LOCALITIES) {
  const k = cityKey(name);
  const prev = BY_KEY.get(k);
  if (!prev || pop > prev.pop) BY_KEY.set(k, { name, lat, lng, pop, code });
  BY_EXACT.set(exactKey(name), { name, lat, lng, pop, code });
}
for (const c of SITE_ONLY) BY_KEY.set(cityKey(c.name), c);
const ALIAS = new Map<string, string>();
for (const [a, target] of Object.entries(ALIAS_RAW)) ALIAS.set(cityKey(a), target);

/** Resolve a city name (any spelling / alias / CBS long form / "X, ישראל") to its verified locality, or null.
 *  Exact key or alias only — no substring guessing ("גן יבנה" is not "יבנה", "אור יהודה" is not "יהוד"). */
export function resolveCity(name?: string | null): GeoCity | null {
  const k = cityKey(name);
  if (!k) return null;
  const direct = BY_EXACT.get(exactKey(String(name))) ?? BY_KEY.get(k);
  if (direct) return direct;
  const target = ALIAS.get(k);
  return target ? BY_KEY.get(cityKey(target)) ?? null : null;
}

/** Same locality? (spelling/alias-insensitive). */
export function sameLocality(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const ca = resolveCity(a), cb = resolveCity(b);
  if (ca && cb) return ca.code === cb.code && ca.name === cb.name;
  return baseKey(a) === baseKey(b); // unknown to the engine: exact spelling only
}

type Place = string | LatLng | null | undefined;
function point(p: Place): LatLng | null {
  if (p == null) return null;
  if (typeof p === 'string') { const c = resolveCity(p); return c ? { lat: c.lat, lng: c.lng } : null; }
  return Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)) ? { lat: Number(p.lat), lng: Number(p.lng) } : null;
}
/** Straight-line km between two places (city names or points); null when either can't be resolved. */
export function distanceKm(a: Place, b: Place): number | null {
  const pa = point(a), pb = point(b);
  return pa && pb ? haversineKm(pa.lat, pa.lng, pb.lat, pb.lng) : null;
}

/** Display rounding for "📍 X ק״מ מ<city>": whole km, never "0" (a different town is at least 1 km away). */
export function roundKm(km: number): number { return Math.max(1, Math.round(km)); }

/** Nearest verified locality to a point (optionally only localities with population ≥ minPop). */
export function nearestLocality(lat: number, lng: number, minPop = 0): { city: GeoCity; km: number } | null {
  let best: { city: GeoCity; km: number } | null = null;
  for (const [name, la, ln, pop, code] of GEO_LOCALITIES) {
    if (pop < minPop) continue;
    const km = haversineKm(lat, lng, la, ln);
    if (!best || km < best.km) best = { city: { name, lat: la, lng: ln, pop, code }, km };
  }
  return best;
}

/** Israel incl. Judea & Samaria / Golan bbox — anything outside is a failed geocode. */
export function inIsraelBBox(lat: number, lng: number): boolean { return lat >= 29.4 && lat <= 33.45 && lng >= 34.2 && lng <= 35.95; }
